<template>
  <div
    class="re-input"
    :class="{
      [`re-${size}`]: size,
      'blink-animation': animate,
      'self-end': !$slots.label && !label && $attrs['self-start'] === undefined,
      'no-bottom-slots': showErrorAsTooltip,
    }"
  >
    <template v-if="$slots.label || label">
      <div class="label all-pointer-events row justify-between">
        <slot
          name="label"
          v-bind="$slots.label ?? {}"
        >
          <p class="row no-wrap">
            {{ label }}
            <re-tooltip
              v-if="tooltip"
              :label="tooltip"
            />
          </p>
        </slot>

        <slot name="label-append" />
      </div>
    </template>

    <QInput
      ref="inputRef"
      :model-value="displayedValue"
      :name="name"
      :type="type !== 'number' ? type : 'text'"
      :rules="activeRules"
      :readonly="readOnlyComputed"
      :disable="disableComputed"
      outlined
      :class="{
        'input-embed-button': 'embed' in $slots,
      }"
      :input-style="{ minWidth: '2em' }"
      :input-class="`text-dark ${size ? `re-${size}` : ''}`"
      :dense="['md', 'sm', 'xs'].includes(size)"
      lazy-rules="ondemand"
      :hide-bottom-space="hideBottomSpace"
      :no-error-icon="showErrorAsTooltip"
      :debounce="debounce"
      v-bind="{ ...attrs }"
      @blur="handleBlur"
      @focus="handleFocus"
      @update:model-value="handleInput"
    >
      <template #prepend>
        <slot name="prepend" />
      </template>

      <template #append>
        <div
          v-if="unit || $slots.append || (showErrorAsTooltip && inputRef?.hasError)"
          class="q-ml-sm row q-gutter-sm"
        >
          <re-tooltip
            v-if="showErrorAsTooltip && inputRef?.hasError"
            is-error
            :tooltip-classes="['bg-negative']"
            :label="errorMessage"
            :size="size"
          />

          <slot name="append" />

          <span
            v-if="unit"
            :class="`re-${size}`"
          >
            {{ unit }}
          </span>
        </div>

        <slot name="embed" />
      </template>

      <slot />

      <template
        v-if="maxlength"
        #counter
      >
        <span :class="`re-${size}`">{{ displayedValue?.toString().length || 0 }}/{{ maxlength }}</span>
      </template>
    </QInput>
  </div>
</template>

<script setup lang="ts">
import type { QInputProps, ValidationRule } from 'quasar'
import type { Ref } from 'vue'
import { computedAsync } from '@vueuse/core'
import { isNil } from 'lodash-es'
import { QInput } from 'quasar'
import { computed, nextTick, ref, useAttrs, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useReadOnly } from '@/composables/useReadOnly'
import { useGlobals } from '@/main'
import reRules from '@/plugins/rules'

// Quasar types are not great, thus the Omit
interface ReInputProps extends Omit<QInputProps, 'modelValue' | 'onUpdate:modelValue'> {
  tooltip?: string
  round?: number | boolean
  animate?: boolean
  showErrorAsTooltip?: boolean
  min?: number
  max?: number
  maxlength?: number
  size?: string
  required?: boolean
  readonly?: boolean
  debounce?: number | string
  disable?: boolean
}

const {
  name,
  min = undefined,
  max = undefined,
  maxlength = undefined,
  required = false,
  tooltip = undefined,
  round = false,
  size = 'lg',
  type = 'text',
  animate = false,
  showErrorAsTooltip = false,
  hideBottomSpace = true,
  rules = [],
  readonly = undefined, // differentiate between explicitly setting readonly and inheriting from useReadOnly
  debounce = undefined,
  disable = false,
} = defineProps<ReInputProps>()

const inputRef = ref() as Ref<QInput>
const isInputActive = ref(false)
const interactedWith = ref(false)
const validate = (val: unknown) => inputRef.value?.validate(val)
const hasError = computed(() => (interactedWith.value ? inputRef.value?.hasError : undefined))

const { $format } = useGlobals()
const { t } = useI18n()
const { isReadOnly } = useReadOnly()

// Source of truth, BE compliant
// Supported modifiers:
// - percent: will display a value multiplied by 100 and divide the user input by
//   100 before emitting it, to allow users to input percentages in a natural way while still keeping the modelValue as a number between 0 and 1
// - currency: will format the number as a currency on blur
// - formatNumber: will format the number on blur
// - precision: will round the number to the specified decimals on blur
// - rounding: will round the number to the nearest specified value on blur (e.g. rounding=100 will round to the nearest 100)
// - required: will add a required validation rule
// - m2: will add m2 unit
const [modelValue, modelModifiers] = defineModel<string | number>()

const isInputTypeNumber = computed(() => type === 'number' || !!modelModifiers?.number)

// avoid getting precision errors when multiplying/dividing by 100 for percent modifier
const fixPrecisionError = (x: number) => Math.round(x * 1e10) / 1e10

const roundValue = (inputValue: number, nearest: number) => Math.round(inputValue / nearest) * nearest

const isInputRounded: Ref<boolean> = computed(() => !!round || !!modelModifiers?.round)

const roundedValue: Ref<number> = computed(() => {
  if (typeof round === 'number')
    return round
  if (typeof modelModifiers?.round === 'number')
    return modelModifiers.round
  return 1
})

const isInputRequired = computed(() => required || !!modelModifiers?.required)

const readOnlyComputed = computed(() => {
  return isNil(readonly) ? isReadOnly.value : readonly
})

const disableComputed = computed(() => {
  return isNil(disable) ? isReadOnly.value : disable
})

// ============================================================================
// VALUE TRANSFORMATION HELPERS
// ============================================================================

// Convert external modelValue to internal display value (e.g., 0.15 → 15 for percent)
function toInternalValue(val: string | number | null | undefined): string | number | null | undefined {
  if (val == null || val === '')
    return val
  if (modelModifiers.percent && typeof val === 'number') {
    return fixPrecisionError(val * 100)
  }
  return val
}

// Convert internal value back to external modelValue (e.g., 15 → 0.15 for percent)
function toExternalValue(val: string | number | null | undefined): string | number | undefined {
  if (val == null || val === '')
    return val === null ? undefined : val
  let result = val
  if (isInputTypeNumber.value) {
    result = Number(val)
    // Partial input like "-" or "1." is not a valid number yet — don't emit
    // a string to the parent (which expects a number), hold off until complete.
    if (isNaN(result as number))
      return undefined
  }
  if (modelModifiers.percent && typeof result === 'number') {
    result = fixPrecisionError(result / 100)
  }
  return result
}

// Parse user input to number (if numeric input type)
function parseInputValue(val: string | number | null | undefined): number | string | null | undefined {
  if (val == null)
    return val
  if (val === '')
    return isInputTypeNumber.value ? null : val
  if (!isInputTypeNumber.value)
    return val
  if (typeof val === 'number')
    return val
  const num = Number(val)
  return isNaN(num) ? val : num
}

// ============================================================================
// STATE MANAGEMENT
// ============================================================================

// Internal editing value - what the user sees/types while editing
// This is the "raw" internal value (e.g., 15 for percent, not 0.15)
const editingValue = ref<string | number | null | undefined>(toInternalValue(modelValue.value))

// Sync editingValue when modelValue changes externally
watch(modelValue, (val) => {
  const currentExternalValue = toExternalValue(parseInputValue(editingValue.value))

  if (!isInputActive.value || currentExternalValue !== val) {
    editingValue.value = toInternalValue(val)
  }
})

// ============================================================================
// DISPLAY VALUE (what q-input shows)
// ============================================================================

// Pure computed getter - formats value for display
const displayedValue = computed<string | number | null | undefined>(() => {
  // While actively editing (and not readonly), show the raw editing value (no formatting)
  if (isInputActive.value && !disableComputed.value) {
    return editingValue.value
  }

  let val = editingValue.value

  // Apply rounding for display (before user interaction)
  if (!interactedWith.value && isInputRounded.value && typeof val === 'number') {
    val = roundValue(val, roundedValue.value)
  }

  // Format for display when not editing
  if (isInputTypeNumber.value && val !== '' && val != null) {
    const numVal = typeof val === 'number' ? val : Number(val)
    if (!isNaN(numVal)) {
      // Apply precision
      if (modelModifiers.precision !== undefined) {
        const precision = Number(modelModifiers.precision) || 2
        val = Math.round(numVal * 10 ** precision) / 10 ** precision
      }

      // Apply currency formatting (display only)
      if (modelModifiers.currency) {
        val = $format.currency(numVal, { style: 'decimal', nearest: false, showCents: 2 })
      }

      // Apply number formatting (display only)
      if (modelModifiers.formatNumber) {
        val = $format.number(numVal)
      }
    }
  }

  return val
})

// ============================================================================
// INPUT HANDLING
// ============================================================================

// Handle user input - parse and validate the RAW value, not formatted
function handleInput(val: string | number | null | undefined) {
  interactedWith.value = true
  editingValue.value = val

  // Parse to get the actual numeric value for validation
  const parsedValue = parseInputValue(val)

  // Validate the parsed (raw) value
  validate(parsedValue)

  // For non-rounded inputs, update modelValue immediately
  // Rounded inputs wait for blur to avoid interfering with typing
  if (!isInputRounded.value) {
    const externalValue = toExternalValue(parsedValue)
    if (inputRef.value && !inputRef.value.hasError) {
      modelValue.value = externalValue
    }
  }
}

function handleBlur() {
  const parsedValue = parseInputValue(editingValue.value)

  // Apply rounding on blur
  if (isInputRounded.value && typeof parsedValue === 'number' && !isNaN(parsedValue)) {
    const rounded = roundValue(parsedValue, roundedValue.value)
    editingValue.value = rounded
  }

  // Validate and update modelValue
  const finalParsed = parseInputValue(editingValue.value)
  if (validate(finalParsed)) {
    modelValue.value = toExternalValue(finalParsed)
  }

  isInputActive.value = false
}

function handleFocus() {
  isInputActive.value = true
  // Don't reset editingValue here - let user continue editing what they typed
  // Only sync from modelValue if editingValue is unset
  editingValue.value ??= toInternalValue(modelValue.value)
}

// ============================================================================
// VALIDATION RULES
// ============================================================================

// Rules always receive the RAW parsed value, never the formatted display string
const activeRules = computed<ValidationRule[]>(() => {
  const retRules: ValidationRule[] = []

  // Wrap each rule to ensure it receives the parsed numeric value
  const wrapRule = (rule: ValidationRule): ValidationRule => {
    // Handle rules that return a boolean directly (e.g., mustBeCoherent returns true when no rejections)
    if (typeof rule !== 'function') {
      return () => rule as boolean | string
    }
    return () => {
      // Always use the raw editingValue, not what q-input passes (which may be formatted)
      const rawVal = parseInputValue(editingValue.value)
      return (rule as (v: unknown) => boolean | string)(rawVal)
    }
  }

  if (isInputRequired.value)
    retRules.push(wrapRule(reRules.required))
  if (isInputTypeNumber.value)
    retRules.push(wrapRule(val => reRules.numeric(val) as boolean | string))
  if (type === 'email')
    retRules.push(val => reRules.email(val) as boolean | string)
  if (min !== undefined)
    retRules.push(wrapRule(reRules.minValue(min)))
  if (max !== undefined)
    retRules.push(wrapRule(reRules.maxValue(max)))
  if (maxlength !== undefined)
    retRules.push(reRules.maxLength(maxlength))

  // Wrap user-provided rules too (handle both functions and boolean/string values)
  retRules.push(...(rules || []).map(rule => (isInputTypeNumber.value ? wrapRule(rule) : rule)))
  return retRules
})

const attrs = useAttrs()
const unit = computed(() => {
  if (attrs.unit)
    return attrs.unit
  if (modelModifiers.percent)
    return t('unit.percent')
  if (modelModifiers.currency)
    return t('unit.euro')
  if (modelModifiers.m2)
    return t('unit.m2')

  return undefined
})

// ⚠️ Unfortunately Quasar doesn't provide a way to access the error message
const errorMessage = computedAsync(async () => {
  if (!inputRef.value?.hasError)
    return ''
  await nextTick()
  const el = inputRef.value?.$el as HTMLElement
  const errorMessage = el?.querySelector('.q-field__messages')?.textContent || ''
  return errorMessage.trim()
}, null)

defineExpose({
  hasError,
  validate,
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
})
</script>

<style lang="scss" scoped>
.re-input {
  &.no-bottom-slots {
    :deep(.q-field__bottom) {
      display: none;
    }
  }

  :deep(.q-field__control) {
    background-color: $base;
    height: 2.858em !important;
    overflow: hidden;
  }

  :deep(.q-field--dense .q-field__control) {
    height: 2.858em !important;
    min-height: 2.858em !important;
  }

  :deep(.q-textarea .q-field__control) {
    height: auto !important;
    padding-inline: 0;
  }

  :deep(.q-field__marginal) {
    height: inherit;
  }

  :deep(textarea) {
    line-height: normal;
  }

  .input-embed-button {
    :deep(.q-field__control) {
      padding-right: 0 !important;
      overflow: hidden;
    }

    :deep(.q-field__append .q-btn) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }

  .label {
    max-height: calc(1.2em + 8px);
    height: calc(1.2em + 8px);

    p {
      font-size: 1em;
      text-wrap: nowrap;
      line-height: 1.2em;
      color: var(--q-secondary);
      font-weight: 500;
    }
  }

  :deep(input::placeholder),
  :deep(textarea::placeholder) {
    color: var(--q-secondary);
  }

  :deep(.q-field--readonly input::placeholder),
  :deep(.q-field--readonly textarea::placeholder) {
    color: transparent;
  }

  &.blink-animation :deep(.q-field__control) {
    animation: blink 2s ease-in-out;
  }

  @keyframes blink {
    0% {
      background-color: transparent;
      box-shadow: none;
    }
    50% {
      background-color: transparent;
      box-shadow: 0 0 0 2px var(--q-primary);
    }
    100% {
      background-color: transparent;
      box-shadow: none;
    }
  }
}

body.screen--xs .re-input .left-right-buttons {
  max-width: inherit;
}
</style>
